import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faArrowLeft, faUsers, faHandshake, faBriefcase, faChartLine, faCalendarAlt, faUser } from '@fortawesome/free-solid-svg-icons';
import { blogPostsData } from '../data/blogData';
import MovingBanner from '../components/MovingBanner';

// Optimized animation variants with better performance
const fadeIn = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1, 
    transition: { 
      duration: 0.4,
      ease: "easeOut" 
    } 
  }
};

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { 
      duration: 0.5, 
      ease: [0.1, 0.4, 0.2, 1] 
    } 
  }
};

const staggerContainer = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  }
};

// Reduce number of particles for better performance
const PARTICLE_COUNT = 10;

const Home: React.FC = () => {
  // Get the latest 3 blog posts for the slider
  const latestBlogPosts = [...blogPostsData]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 3);
  
  // State for blog slider
  const [currentBlogSlide, setCurrentBlogSlide] = useState(0);
  
  // Blog slider controls
  const nextBlogSlide = () => {
    setCurrentBlogSlide((prev: number) => 
      prev === latestBlogPosts.length - 1 ? 0 : prev + 1
    );
  };
  
  const prevBlogSlide = () => {
    setCurrentBlogSlide((prev: number) => 
      prev === 0 ? latestBlogPosts.length - 1 : prev - 1
    );
  };

  const services = [
    {
      icon: faUsers,
      title: "Permanent Staffing",
      description: "Locate highly skilled individuals that share your company's values and objectives.",
      link: "/services",
      color: "bg-primary"
    },
    {
      icon: faBriefcase,
      title: "Temporary & Contract Staffing",
      description: "Employ temporary workers for particular tasks or demand throughout certain seasons.",
      link: "/services",
      color: "bg-secondary"
    },
    {
      icon: faHandshake,
      title: "Executive Search",
      description: "Find elite executives and management experts to lead your organization.",
      link: "/services",
      color: "bg-accent"
    },
    {
      icon: faChartLine,
      title: "Industry-Specific Recruitment",
      description: "Tailored employment solutions for various industries including manufacturing, retail, healthcare, IT, and finance.",
      link: "/services",
      color: "bg-secondary"
    }
  ];

  return (
    <div className="pt-20">
      {/* Moving Banner */}
      <MovingBanner 
        text="We proudly collaborate with sub-vendors and partners to expand opportunities and deliver excellence!" 
        backgroundColor="bg-gradient-to-r from-[#B01C1C] via-[#483D8B] to-[#00A7D1]"
        speed={25}
      />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Particle Background Effect - Optimized */}
        <div className="particles" aria-hidden="true">
          {[...Array(PARTICLE_COUNT)].map((_, index) => (
            <motion.div
              key={index}
              className={`absolute rounded-full ${
                index % 4 === 0 ? 'bg-primary/20' : 
                index % 4 === 1 ? 'bg-secondary/20' : 
                index % 4 === 2 ? 'bg-accent/20' : 'bg-gray-400/20'
              }`}
              style={{
                width: `${10 + Math.random() * 20}px`,
                height: `${10 + Math.random() * 20}px`,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              initial={{ opacity: 0.4 }}
              animate={{
                x: [0, Math.random() * 30 - 15],
                y: [0, Math.random() * 30 - 15],
                transition: {
                  repeat: Infinity,
                  repeatType: "reverse",
                  duration: 8,
                  ease: "linear"
                }
              }}
            />
          ))}
        </div>

        <div className="container relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              className="text-center lg:text-left"
            >
              <h1 className="mb-6">
                <span className="block company-name">Skill Point Consultants</span>
                <span className="block company-tagline">Your Partner in Professional Growth</span>
              </h1>
              <motion.p 
                variants={fadeIn}
                className="text-lg md:text-xl text-gray-700 mb-8"
              >
                Nestled in the heart of Haryana, Gurgaon is India's leading corporate and business hub. We help professionals and companies find the ideal fit by bridging the gap between talent and success.
              </motion.p>
              <motion.div
                variants={staggerContainer}
                className="flex flex-col items-start gap-4 w-full max-w-xs"
              >
                <motion.div variants={fadeIn} className="w-full border border-[#3498db] rounded-md px-4 py-2 text-center">
                  <h3 className="text-xl font-semibold text-[#3498db] m-0">
                    I'm an Employer
                  </h3>
                </motion.div>
                <motion.div variants={fadeIn} className="w-full border border-[#3498db] rounded-md px-4 py-2 text-center">
                  <h3 className="text-xl font-semibold text-[#3498db] m-0">
                    I'm a Job Seeker
                  </h3>
                </motion.div>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="hidden lg:block"
            >
              <div className="relative">
                <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-primary/10 rounded-full filter blur-3xl"></div>
                <motion.div 
                  className="w-full h-96 rounded-lg flex items-center justify-center overflow-hidden shadow-lg"
                  initial="hidden"
                  animate="visible"
                  variants={fadeIn}
                >
                  <motion.div
                    animate={{ 
                      y: [0, -5, 0],
                      transition: { 
                        repeat: Infinity, 
                        duration: 4, 
                        ease: "easeInOut" 
                      }
                    }}
                    className="w-full h-full"
                  >
                    <img 
                      src="https://talentgenie.in/wp-content/uploads/2024/05/960x0.jpg" 
                      alt="Connecting Talent" 
                      className="w-full h-full object-cover"
                    />
                  </motion.div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Preview Section - With optimized animations */}
      <section className="section bg-gray-50">
        <div className="container">
          <motion.h2 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            className="section-title"
          >
            Our Services
          </motion.h2>
          
          {/* Use a div instead of motion.div for the container to reduce animation layers */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ 
                  duration: 0.4,
                  delay: index * 0.1,
                  ease: [0.1, 0.4, 0.2, 1]
                }}
                whileHover={{ 
                  y: -8,
                  transition: { duration: 0.2, ease: "easeOut" } 
                }}
                className="card group"
              >
                <div className={`w-full h-2 ${service.color}`}></div>
                <div className="p-6">
                  <div className={`w-16 h-16 rounded-lg ${service.color} bg-opacity-10 flex items-center justify-center mb-4 group-hover:bg-opacity-20 transition-all duration-300`}>
                    <FontAwesomeIcon icon={service.icon} className={`text-2xl ${service.color === 'bg-primary' ? 'text-primary' : service.color === 'bg-secondary' ? 'text-secondary' : 'text-accent'}`} />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-6">{service.description}</p>
                  <Link 
                    to={service.link} 
                    className="flex items-center font-medium text-primary hover:text-primary-light"
                  >
                    Learn more 
                    <FontAwesomeIcon icon={faArrowRight} className="ml-2 text-sm" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Blog Posts Slider */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            className="text-center mb-12"
          >
            <h2 className="section-title">Latest Insights</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Stay updated with the latest trends and insights from the staffing and recruitment industry
            </p>
          </motion.div>

          <div className="relative">
            <div className="overflow-hidden rounded-lg shadow-lg">
              {latestBlogPosts.map((post, index) => (
                <div 
                  key={post.id}
                  className={`transition-opacity duration-500 ${currentBlogSlide === index ? 'block' : 'hidden'}`}
                >
                  <div className="relative">
                    <img 
                      src={post.image} 
                      alt={post.title} 
                      className="w-full h-[400px] object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/70 to-black/50 flex flex-col justify-end p-8 text-white">
                      <div className="mb-2">
                        <span className="px-3 py-1 bg-primary rounded-full text-sm font-medium">
                          {post.category}
                        </span>
                        <span className="ml-3 text-sm">
                          <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" />
                          {post.date}
                        </span>
                      </div>
                      <h3 className="text-2xl md:text-3xl font-bold mb-3 text-white drop-shadow-md">
                        {post.title}
                      </h3>
                      <p className="text-white mb-4 line-clamp-2 drop-shadow-sm">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <FontAwesomeIcon icon={faUser} className="mr-2" />
                          <span>{post.author}</span>
                        </div>
                        <Link 
                          to={`/blog/${post.id}`} 
                          className="text-white hover:text-primary transition-colors flex items-center"
                        >
                          Read more <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Slider Controls */}
            <div className="absolute top-1/2 -translate-y-1/2 left-4">
              <button 
                onClick={prevBlogSlide} 
                className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm text-white flex items-center justify-center hover:bg-white/30 transition-all"
                aria-label="Previous slide"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>
            </div>
            <div className="absolute top-1/2 -translate-y-1/2 right-4">
              <button 
                onClick={nextBlogSlide} 
                className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm text-white flex items-center justify-center hover:bg-white/30 transition-all"
                aria-label="Next slide"
              >
                <FontAwesomeIcon icon={faArrowRight} />
              </button>
            </div>

            {/* Dots Indicator */}
            <div className="flex justify-center mt-4">
              {latestBlogPosts.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentBlogSlide(index)}
                  className={`w-3 h-3 rounded-full mx-1 transition-all ${
                    currentBlogSlide === index ? "bg-primary w-6" : "bg-gray-300"
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>

          <div className="text-center mt-8">
            <Link to="/blog" className="btn-teal-outline inline-flex items-center">
              View All Articles <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-20 bg-gradient-to-r from-[#B01C1C] via-[#483D8B] to-[#00A7D1] text-white">
        <div className="container text-center">
          <motion.h2 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            className="text-3xl md:text-4xl font-bold mb-6 text-white"
          >
            Ready to Find the Perfect Match?
          </motion.h2>
          <motion.p 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
            className="text-lg md:text-xl mb-10 max-w-3xl mx-auto text-white/90"
          >
            Whether you're seeking top talent or looking for your next career opportunity, Skill Point Consultants is here to help.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          >
            <Link to="/contact" className="inline-block px-6 py-3 bg-[#B01C1C] hover:bg-[#C02C2C] text-white font-medium rounded-md transition-all duration-300">
              Get in Touch Today
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home; 